import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Link, Redirect } from 'react-router-dom';
import useQuery from '../../hooks/use-query';
import Loader from '../loader';
import Paginator from '../paginator';
import Select from '../select';
import api from '../../api';
import './library-book-version-list.css';

let searchUpdateTimeout = null;

const statusesLabels = {
  draft: 'En préparation',
  published: 'En ligne',
  archived: 'Archivé',
};

const BookStatus = ({ status }) => (
  <span className={`book-list_status-badge book-list_status-badge--${status}`}>
    {statusesLabels[status]}
  </span>
);

const NewsletterToggle = ({ book }) => {
  const [state, setState] = useState(book.includedInNewsletter);
  const [isLoading, setIsLoading] = useState(false);

  const generateClassname = () => {
    let className = 'book-list_newsletter-toggle';

    if (state) {
      className += ' book-list_newsletter-toggle--active';
    }

    if (isLoading) {
      className += ' book-list_newsletter-toggle--loading';
    }

    return className;
  };

  const toggle = () => {
    setIsLoading(true);
    api.libraryBookVersions.put(
      {
        id: book.id,
        book: {
          includedInNewsletter: !state,
        },
      },
      () => {
        setState(!state);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  return (
    <span
      className={generateClassname()}
      onClick={e => {
        e.preventDefault();

        if (!isLoading) {
          toggle();
        }
      }}
    >
      <i className="book-list_newsletter-toggle-icon material-icons">{state ? 'toggle_on' : 'toggle_off'}</i>
    </span>
  );
};

const BookList = () => {
  const query = useQuery();
  const page = query.get('page') ? query.get('page') : 1;
  const itemsPerPage = query.get('itemsPerPage') ? query.get('itemsPerPage') : 25;
  const search = query.get('search') ? query.get('search') : '';
  const status = query.get('status') ? query.get('status') : '';
  const sortBy = query.get('sortBy') ? query.get('sortBy') : '';
  const sortDir = query.get('sortDir') ? query.get('sortDir') : 'DESC';
  const generateRouteParams = useCallback(params => {
    let routeParams = [];

    if (params.search || (search && !params.hasOwnProperty('search'))) {
      routeParams.push(`search=${params.hasOwnProperty('search') ? params.search : search}`);
    }

    if (params.status || (status && !params.hasOwnProperty('status'))) {
      routeParams.push(`status=${params.hasOwnProperty('status') ? params.status : status}`);
    }

    if (params.sortBy || (sortBy && !params.hasOwnProperty('sortBy'))) {
      routeParams.push(`sortBy=${params.hasOwnProperty('sortBy') ? params.sortBy : sortBy}`);
    }

    if (params.sortDir || (sortDir && !params.hasOwnProperty('sortDir'))) {
      routeParams.push(`sortDir=${params.hasOwnProperty('sortDir') ? params.sortDir : sortDir}`);
    }

    if (params.itemsPerPage || (itemsPerPage && !params.hasOwnProperty('itemsPerPage'))) {
      routeParams.push(`itemsPerPage=${params.hasOwnProperty('itemsPerPage') ? params.itemsPerPage : itemsPerPage}`);
    }

    if (params.page || (page && !params.hasOwnProperty('page'))) {
      routeParams.push(`page=${params.hasOwnProperty('page') ? params.page : page}`);
    }

    return `/librairie/livres?${routeParams.join('&')}`;
  }, [search, sortBy, sortDir, itemsPerPage, page, status]);
  const [redirect, setRedirect] = useState(null);
  const [books, setBooks] = useState([]);
  const [pages, setPages] = useState(0);
  const [nbResults, setNbResults] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const changeSort = by => {
    let dir = by === sortBy ? sortDir === 'DESC' ? 'ASC' : 'DESC' : 'ASC';

    setRedirect(generateRouteParams({sortBy: by, sortDir: dir, page: 1}));
  };

  useEffect(() => {
    setRedirect(null);
    setIsLoading(true);
    api.libraryBookVersions.list(
      {
        search: search,
        page: page,
        items_per_page: itemsPerPage,
        sort_by: sortBy,
        sort_dir: sortDir,
        filters: {
          status,
        },
      },
      data => {
        setBooks(data.items);
        setPages(data.pageCount);
        setNbResults(data.totalCount);
        setError(null);
        setIsLoading(false);
      },
      err => {
        setError(err);
        setIsLoading(false);
      },
    );
  }, [search, itemsPerPage, page, sortBy, sortDir, status]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <div className="book-list">
      <div className="book-list_nav">
        <div className="book-list_search">
          <input
            className="book-list_search-input"
            type="text"
            onChange={e => {
              let val = e.target.value;
              setSearchInput(val);
              clearTimeout(searchUpdateTimeout);
              searchUpdateTimeout = setTimeout(() => {
                setRedirect(generateRouteParams({search: val, page: 1}));
              }, 600);
            }}
            placeholder="Rechercher des livres"
            value={searchInput}
          />
          <Select
            name="status"
            options={[
              {
                label: 'Tous les statuts',
                value: '',
              },
              {
                label: 'En préparation',
                value: 'draft',
              },
              {
                label: 'Publié',
                value: 'published',
              },
              {
                label: 'Archivé',
                value: 'archived',
              },
            ]}
            onChange={e => setRedirect(generateRouteParams({status: e.target.value, page: 1}))}
            label=""
            value={status}
          />
        </div>

        <div className="book-list_pagination">
          <span className="book-list_count">Livres {(page - 1) * itemsPerPage + 1} à {(page - 1) * itemsPerPage + Number(itemsPerPage, 10)} sur {nbResults} résultats</span>
          <select
            className="book-list_items-per-page"
            id="ipp"
            onChange={e => setRedirect(generateRouteParams({itemsPerPage: e.target.value, page: 1}))}
            value={itemsPerPage}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {isLoading && <Loader />}

      {!isLoading && error && <p className="book-list_error">Une erreur s'est produite, essayez de réactualiser la page.</p>}

      {!isLoading && !error && (
        <div>
          <div className="book-list_table">
            <div className="book-list_header">
              <div
                className="book-list_header-cell book-list_header-cell--sortable book-list_cell--id"
                onClick={() => changeSort('id')}
              >
                ID
                {sortBy === 'id' && (
                  <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="book-list_header-cell book-list_header-cell--sortable book-list_cell--ean"
                onClick={() => changeSort('ean')}
              >
                EAN
                {sortBy === 'ean' && (
                  <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div
                className="book-list_header-cell book-list_header-cell--sortable book-list_cell--date"
                onClick={() => changeSort('publicationDate')}
              >
                Date de publication
                {sortBy === 'publicationDate' && (
                  <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

              <div className="book-list_header-cell book-list_cell--name">
                <div
                  className="book-list_sub_cell book-list_header-cell--sortable book-list_sub_cell--name"
                  onClick={() => changeSort('title')}
                >
                  Titre
                  {sortBy === 'title' && (
                    <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                  )}
                </div>

                <div
                  className="book-list_sub_cell book-list_header-cell--sortable book-list_sub_cell--name"
                  onClick={() => changeSort('author')}
                >
                  Auteur
                  {sortBy === 'author' && (
                    <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                  )}
                </div>
              </div>

              {/* <div
                className="book-list_header-cell book-list_header-cell--sortable book-list_cell--view"
                onClick={() => changeSort('nbView')}
              >
                Vues
                {sortBy === 'nbView' && (
                  <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div> */}

              <div className="book-list_header-cell book-list_cell--has-document">
                Extrait
              </div>

              <div
                className="book-list_header-cell book-list_header-cell--sortable book-list_cell--active"
                onClick={() => changeSort('includedInNewsletter')}
              >
                Newsletter
                {sortBy === 'includedInNewsletter' && (
                  <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>


              <div
                className="book-list_header-cell book-list_header-cell--sortable book-list_cell--active"
                onClick={() => changeSort('status')}
              >
                Statut
                {sortBy === 'status' && (
                  <i className="book-list_sort-icon material-icons">{sortDir === 'ASC' ? 'expand_less' : 'expand_more'}</i>
                )}
              </div>

            </div>

            {books.length > 0 && (
              <div>
                {books.map(book => (
                  <div
                    className="book-list_row"
                    key={book.id}
                  >
                    <Link
                      className="book-list_cell book-list_cell--id"
                      title="Éditer le livre"
                      to={`/librairie/livres/${book.id}/modifier`}
                    >
                      {book.id}
                    </Link>

                    <Link
                      className="book-list_cell book-list_cell--ean"
                      title="Éditer le livre"
                      to={`/librairie/livres/${book.id}/modifier`}
                    >
                      {book.ean}
                    </Link>

                    <Link
                      className="book-list_cell book-list_cell--date"
                      title="Éditer le livre"
                      to={`/librairie/livres/${book.id}/modifier`}
                    >
                      {book.publicationDate ? moment(book.publicationDate).format('DD/MM/YYYY - HH:mm') : ''}
                    </Link>

                    <Link
                      className="book-list_cell"
                      title="Éditer le livre"
                      to={`/librairie/livres/${book.id}/modifier`}
                    >
                        <div className="book-list_sub_cell book-list_sub_cell--name">
                          {book.title}
                        </div>
                        <div className="book-list_sub_cell book-list_sub_cell--parent">
                          {book.authorsOnlyNames}
                        </div>
                    </Link>

                    {/* <Link
                      className="book-list_cell book-list_cell--view"
                      title="Éditer le livre"
                      to={`/librairie/livres/${book.id}/modifier`}
                    >
                      {book.nbView}
                    </Link> */}

                    <Link
                      className="book-list_cell book-list_cell--has-document"
                      title="Éditer le livre"
                      to={`/librairie/livres/${book.id}/modifier`}
                    >
                      {book.hasDocument ? <span className="book-list_cell--has-document-badge book-list_cell--has-document-badge--yes">Oui</span> : <span className="book-list_cell--has-document-badge book-list_cell--has-document-badge--no">Non</span>}
                    </Link>

                    <div className="article-list_cell article-list_cell--newsletter">
                      <NewsletterToggle book={book} />
                    </div>

                    <div className="book-list_cell book-list_cell--active">
                      <BookStatus status={book.status} />
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>

          <Paginator
            currentPage={page}
            maxPage={pages}
            routePattern={generateRouteParams({page: '[page]'})}
          />
        </div>
      )}

      {books.length === 0 && !isLoading && (
        <p className="book-list_no-result">Aucun résultat.</p>
      )}
    </div>
  );
};

export default BookList;
